'use client'

import { useEffect, type ReactNode } from 'react'

import { useIntl } from 'react-intl'
import { toast } from 'react-toastify'
import { styled } from 'styled-components'
import { useBoolean } from 'usehooks-ts'

import { BidStatus } from '@b-stock/fe-core/bstock-next/queries'
import {
  AnalyticsEntityType,
  AnalyticsSource,
  useAnalyticsContext,
  Button,
  Modal,
  SpinnerDots,
  showSnackbar,
} from '@b-stock/fe-core/bstock-react'
import { Breakpoints, Colors, Typography } from '@b-stock/fe-core/design-system'

import { UnAuthWarningModal } from '@/buyer-portal/components/features/UnAuthWarningModal'
import useAccountId from '@/buyer-portal/helpers/useAccountId'
import { ModalContent, ModalFooter } from '@/components/common/modal/ui'
import { useOptionalListingSearch } from '@/components/listings/search/ListingSearchProvider'
import { getAppliedThreshold } from '@/components/listings/search/ListingSearchProvider/categoryThreshold'
import { attAccessRouteProperty } from '@/components/storefrontAccess/attAccessRoute'
import { useBuyerAttAccessRoute } from '@/components/storefrontAccess/useAttAccessRoute'
import { isAttStorefront } from '@/components/termsRestrictions/termsPoints/storefrontSlugs'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'
import { useTrackToastView } from '@/helpers/telemetry/useTrackToastView'

import type { BulkBiddableListing } from './types'
import { useBulkBidMutations } from './useBulkBidMutations'

const StyledModal = styled(Modal)`
  @media ${Breakpoints.min.medium} {
    max-width: min(90vw, 37.5rem);
  }
`

const BodyText = styled.p`
  ${Typography.Body2}
  margin: 0;
`

const Bold = styled.strong`
  font-weight: 600;
`

const ListingsBox = styled.div`
  margin-top: 1rem;
  border: 1px solid ${Colors.Semantic.Neutral.light_grey};
  border-radius: 0.25rem;
  padding: 0.75rem 1rem;
`

const ListingsBoxHeader = styled.p`
  ${Typography.Body3_Semibold}
  margin: 0 0 0.5rem;
`

const ListingItem = styled.p`
  ${Typography.Body3}
  margin: 0;
  color: ${Colors.Semantic.Neutral.dark_grey};

  & + & {
    border-top: 1px solid ${Colors.Semantic.Neutral.light_grey};
    padding-top: 0.5rem;
    margin-top: 0.5rem;
  }
`

const StatusNote = styled.span`
  font-style: italic;
  color: ${Colors.Semantic.Success.copy};
  margin-left: 0.25rem;
`

type BulkBidModalProps = {
  listings: BulkBiddableListing[]
  onClose: () => void
  onSuccess: (failedListingIds: string[]) => void
  /**
   * The storefront whose page the modal opens on (ListView on a storefront page). Search results
   * carry no slug, so this is how a bulk bid from the AT&T storefront's own grid is recognised.
   */
  storefrontSlug?: string | null
}

function formatListingRow(
  listing: BulkBiddableListing,
  formatMessage: ReturnType<typeof useIntl>['formatMessage']
) {
  const location = listing.sellerCity ?? listing.region
  return location
    ? formatMessage(
        { id: 'Listings.ListView.bulkActions.bulkBidModal.listingRow' },
        { title: listing.title, location }
      )
    : listing.title
}

export function BulkBidModal({
  listings,
  onClose,
  onSuccess,
  storefrontSlug,
}: BulkBidModalProps) {
  const { formatMessage } = useIntl()
  const isLoggedIn = !!useAccountId()
  const { track, trackButtonClicked } = useAnalytics()

  // Read threshold from filter state to attach to bid events.
  // useOptionalListingSearch returns null when rendered outside a provider.
  const listingSearch = useOptionalListingSearch()
  const thresholdForBid = getAppliedThreshold(
    listingSearch?.state.filters ?? {}
  )
  const { screenName } = useAnalyticsContext()
  const trackToastView = useTrackToastView()
  const {
    setTrue: openUnAuthModal,
    setFalse: closeUnAuthModal,
    value: showUnAuthModal,
  } = useBoolean(false)
  const {
    submit,
    isPending,
    biddableCount: count,
  } = useBulkBidMutations(listings)

  // One decision per account: the event carries the buyer's route once when the modal opens on
  // the AT&T storefront page or any selected listing is on an AT&T storefront.
  const attAccessRoute = useBuyerAttAccessRoute(
    isAttStorefront(storefrontSlug) ||
      listings.some((listing) => isAttStorefront(listing.storefrontSlug))
  )

  const listingIdPairings = listings.map(
    (listing) => `${listing.storefrontName}: ${listing.listingId}`
  )

  useEffect(() => {
    trackButtonClicked({
      buttonName: 'bulk_bid_confirmation_viewed',
      screenName,
      source: AnalyticsSource.HOME_PORTAL,
      entityType: AnalyticsEntityType.BUYER,
      selection_count: listings.length,
      listing_id_pairings: listingIdPairings,
    })
    // Intentionally empty deps — fires once on modal open
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleCancel = () => {
    trackButtonClicked({
      buttonName: 'bulk_bid_cancelled',
      screenName,
      source: AnalyticsSource.HOME_PORTAL,
      entityType: AnalyticsEntityType.BUYER,
      selection_count: listings.length,
      listing_id_pairings: listingIdPairings,
    })
    onClose()
  }

  const handleSubmit = async () => {
    trackButtonClicked({
      buttonName: 'bulk_bid_confirmed',
      screenName,
      source: AnalyticsSource.HOME_PORTAL,
      entityType: AnalyticsEntityType.BUYER,
      selection_count: listings.length,
      listing_id_pairings: listingIdPairings,
      ...attAccessRouteProperty(attAccessRoute),
    })

    try {
      const { failedListingIds, total, bidIds } = await submit()

      // All selected listings were already WINNING/WON — nothing to bid on.
      // No toast_view fires here; bulk_bid_confirmed already fired above (user intent).
      // Data Eng: expect orphaned bulk_bid_confirmed with no matching toast_view when total === 0.
      if (total === 0) {
        onSuccess([])
        onClose()
        return
      }

      const succeeded = total - failedListingIds.length

      track('Bulk_Bids_Placed', {
        screenName,
        source: AnalyticsSource.HOME_PORTAL,
        entityType: AnalyticsEntityType.BUYER,
        total_selected: listings.length,
        succeeded_count: succeeded,
        failed_count: failedListingIds.length,
        bid_ids: bidIds,
        listing_id_pairings: listingIdPairings,
        ...(thresholdForBid !== null && { threshold: thresholdForBid }),
      })

      if (failedListingIds.length === 0) {
        const successMessage = formatMessage(
          { id: 'Listings.ListView.bulkActions.bulkBidModal.successToast' },
          { count: succeeded }
        )
        const id = showSnackbar({
          content: successMessage,
          options: { type: 'success', autoClose: true },
        })
        // SnackbarContainer's hideProgressBar suppresses the CSS animation that
        // drives react-toastify's built-in auto-dismiss; setTimeout is a hard-deadline fallback.
        setTimeout(() => toast.dismiss(id), 6_000)
        trackToastView('Success', successMessage, { bid_ids: bidIds })
        onSuccess([])
        onClose()
      } else if (failedListingIds.length < total) {
        const partialMessage = formatMessage(
          {
            id: 'Listings.ListView.bulkActions.bulkBidModal.partialSuccessToast',
          },
          { succeeded, failed: failedListingIds.length }
        )
        const id = showSnackbar({
          content: partialMessage,
          options: { type: 'default', autoClose: 'long' },
        })
        setTimeout(() => toast.dismiss(id), 10_000)
        trackToastView('Partial_Failure', partialMessage, { bid_ids: bidIds })
        onSuccess(failedListingIds)
        onClose()
      } else {
        const errorMessage = formatMessage({
          id: 'Listings.ListView.bulkActions.bulkBidModal.errorToast',
        })
        const id = showSnackbar({
          content: errorMessage,
          options: { type: 'error', autoClose: 'long' },
        })
        setTimeout(() => toast.dismiss(id), 10_000)
        trackToastView('Failure', errorMessage, { bid_ids: [] })
        onClose()
      }
    } catch {
      const errorMessage = formatMessage({
        id: 'Listings.ListView.bulkActions.bulkBidModal.errorToast',
      })
      const id = showSnackbar({
        content: errorMessage,
        options: { type: 'error', autoClose: 'long' },
      })
      setTimeout(() => toast.dismiss(id), 10_000)
      trackToastView('Failure', errorMessage, { bid_ids: [] })
      onClose()
    }
  }

  return (
    <StyledModal closeModal={isPending ? () => {} : handleCancel}>
      <Modal.Header>
        {formatMessage({
          id: 'Listings.ListView.bulkActions.bulkBidModal.modalTitle',
        })}
      </Modal.Header>

      <ModalContent>
        <BodyText>
          {formatMessage(
            { id: 'Listings.ListView.bulkActions.bulkBidModal.modalBody' },
            {
              count,
              bold: (chunks: ReactNode) => <Bold>{chunks}</Bold>,
            }
          )}
        </BodyText>

        <ListingsBox>
          <ListingsBoxHeader>
            {formatMessage({
              id: 'Listings.ListView.bulkActions.bulkBidModal.listingsIncluded',
            })}
          </ListingsBoxHeader>
          {listings.map((listing) => (
            <ListingItem key={listing.listingId}>
              {formatListingRow(listing, formatMessage)}
              {listing.bidStatus === BidStatus.WON && (
                <StatusNote>
                  {formatMessage({
                    id: 'Listings.ListView.bulkActions.bulkBidModal.listingRowWon',
                  })}
                </StatusNote>
              )}
            </ListingItem>
          ))}
        </ListingsBox>
      </ModalContent>

      <ModalFooter>
        <Modal.Actions alignment="end">
          <Button
            type="button"
            appearance="secondary"
            onClick={handleCancel}
            disabled={isPending}
          >
            {formatMessage({
              id: 'Listings.ListView.bulkActions.bulkBidModal.cancel',
            })}
          </Button>
          <Button
            type="button"
            onClick={isLoggedIn ? handleSubmit : openUnAuthModal}
            disabled={isPending || (isLoggedIn && count === 0)}
            aria-busy={isPending}
          >
            {isPending && <SpinnerDots />}
            {isLoggedIn
              ? formatMessage(
                  {
                    id: isPending
                      ? 'Listings.ListView.bulkActions.bulkBidModal.ctaPending'
                      : 'Listings.ListView.bulkActions.bulkBidModal.cta',
                  },
                  { count }
                )
              : formatMessage({
                  id: 'Listings.ListView.bulkActions.bulkBidModal.signInToBid',
                })}
          </Button>
        </Modal.Actions>
      </ModalFooter>

      {showUnAuthModal && (
        <UnAuthWarningModal
          closeUnAuthModal={closeUnAuthModal}
          reason={formatMessage({
            id: 'Listings.ListView.bulkActions.bulkBidModal.unAuthReason',
          })}
        />
      )}
    </StyledModal>
  )
}
